import { useState } from "react";
import { motion } from "framer-motion";
import { Ruler } from "lucide-react";
import { IMAGES, PROTOTYPE_STEPS, PROTOTYPE_CHIPS } from "@/data";
import TiltImage from "@/components/TiltImage";

export default function Prototypes() {
    const [concept, setConcept] = useState("a");

    return (
        <div className="mt-20" data-testid="prototype-section">
            <p className="text-sm font-semibold tracking-[0.3em] uppercase text-sky-200 mb-4" data-testid="prototype-eyebrow">
                Concept Development
            </p>
            <h3 className="font-display font-semibold text-3xl md:text-4xl" data-testid="prototype-heading">
                The River Guardian prototypes
            </h3>
            <p className="mt-4 text-lg text-slate-400 max-w-3xl leading-relaxed">
                Hand-drawn concept studies for the Eora Guardian, a whale shark-inspired hull wearing commissioned
                Indigenous artwork, built around a deep catchment system that traps litter below the waterline.
            </p>

            <div className="mt-8 flex gap-3" data-testid="prototype-tabs">
                {[
                    ["a", "Concept A, Catchment Study"],
                    ["b", "Concept B, Filtration Study"],
                ].map(([key, label]) => (
                    <button
                        key={key}
                        data-testid={`prototype-tab-${key}`}
                        onClick={() => setConcept(key)}
                        className={`px-5 py-2.5 rounded-full text-sm font-semibold border transition-colors ${
                            concept === key
                                ? "bg-sky-400 text-[#020D18] border-sky-400"
                                : "border-[#1C3A54] text-slate-300 hover:border-sky-300 hover:text-sky-200"
                        }`}
                    >
                        {label}
                    </button>
                ))}
            </div>

            <motion.div
                key={concept}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.45 }}
                className="mt-6 bg-[#06182A] border border-[#12283C] rounded-xl p-4 md:p-6"
                data-testid="prototype-viewer"
            >
                <TiltImage
                    src={concept === "a" ? IMAGES.prototype1 : IMAGES.prototype2}
                    alt={concept === "a" ? "River Guardian prototype concept A, catchment study sketch" : "River Guardian prototype concept B, filtration study sketch"}
                    className="w-full rounded-lg"
                    testid="prototype-image"
                />
            </motion.div>

            <div className="mt-6 flex flex-wrap gap-3" data-testid="prototype-chips">
                {PROTOTYPE_CHIPS.map((chip, i) => (
                    <span
                        key={chip}
                        data-testid={`prototype-chip-${i}`}
                        className="inline-flex items-center gap-2 text-sm px-4 py-2 rounded-full border border-sky-400/40 text-sky-200 bg-sky-400/10"
                    >
                        <Ruler className="w-3.5 h-3.5" />
                        {chip}
                    </span>
                ))}
            </div>

            <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="prototype-steps">
                {PROTOTYPE_STEPS.map((step, i) => (
                    <motion.div
                        key={step.title}
                        initial={{ opacity: 0, y: 16 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-40px" }}
                        transition={{ duration: 0.45, delay: i * 0.08 }}
                        className="bg-[#06182A] border border-[#12283C] rounded-xl p-5 hover:border-sky-400/50 transition-colors"
                        data-testid={`prototype-step-${i}`}
                    >
                        <p className="font-display font-semibold text-2xl text-sky-200">{i + 1}.</p>
                        <p className="mt-2 font-semibold text-slate-100">{step.title}</p>
                        <p className="mt-1 text-sm text-slate-400 leading-relaxed">{step.detail}</p>
                    </motion.div>
                ))}
            </div>
        </div>
    );
}
